import { AsDropdown } from '../inputs/as-dropdown.ts';
import '../inputs/as-option.ts';
import '../overlays/as-tooltip-wrapper.ts';
import { internalData } from '../../store/store.ts';
import { sendEv } from '../../data/events.ts';

/**
 * as-lang-selector
 *
 * Compact language picker built on as-dropdown, wired into the as-app localization
 * system (see docs/localization.md). Shows abbreviated codes (EN, DE, ...) with no
 * visible label -- "Select Language" is a hover tooltip instead, using an
 * as-tooltip-wrapper around the dropdown.
 *
 * Only en/de are listed. Add a language here once it has a real translation
 * source behind it: listing one with nothing to show would just leave the UI
 * untranslated after picking it.
 *
 * `label-default-text` / `label-dict-key` are still the attributes to set,
 * same names as as-dropdown itself uses — they now feed the tooltip text
 * rather than a visible label.
 */
const SUPPORTED_LANGS: Record<string, string> = {
  en: 'EN',
  de: 'DE',
};

export class AsLangSelector extends HTMLElement {
  static readonly tag = 'as-lang-selector' as const;

  private _dropdown: AsDropdown | null = null;
  private _tooltip: HTMLElement | null = null;
  // internalData.language is the single source of truth (see localization-mixin.ts);
  // this just tracks what's currently reflected in the dropdown, to no-op
  // redundant setLanguage() calls (e.g. the store echoing back the change
  // this element itself just triggered via handleDropdownChange).
  private _currentLanguage: string = 'en';
  private _unsubLanguage: (() => void) | null = null;

  static get observedAttributes() {
    return ['label-default-text', 'label-dict-key', 'disabled'];
  }

  connectedCallback() {
    // Read the already-active language directly — no event round-trip to
    // race against whatever's listening (or not yet listening) on the other
    // end. createUI() below uses this to pre-select the right option.
    this._currentLanguage = internalData.language;

    if (!this._dropdown) {
      this._createUI();
      this._dropdown!.addEventListener('as-input-change', this._handleDropdownChange);
    }

    this._unsubLanguage = internalData.subscribeKey('language', (lang: string) => this.setLanguage(lang));
  }

  disconnectedCallback() {
    this._unsubLanguage?.();
    this._unsubLanguage = null;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue || !this._dropdown || !this._tooltip) return;

    if (name === 'disabled') {
      if (newValue === null) {
        this._dropdown.removeAttribute('disabled');
      } else {
        this._dropdown.setAttribute('disabled', newValue);
      }
      return;
    }

    const tooltipAttr = name === 'label-default-text' ? 'default-text' : 'dict-key';
    if (newValue === null) {
      this._tooltip.removeAttribute(tooltipAttr);
    } else {
      this._tooltip.setAttribute(tooltipAttr, newValue);
    }
  }

  private _handleDropdownChange = (event: Event) => {
    const newLang = (event as CustomEvent).detail?.value;
    if (!newLang || newLang === this._currentLanguage) return;

    this._currentLanguage = newLang;
    sendEv(this, 'as-change-language', {
      detail: { lang: newLang }
    });
  };

  private _createUI(): void {
    const dropdown = document.createElement('as-dropdown') as AsDropdown;
    dropdown.setAttribute('no-label', '');
    // The language is app chrome, not project state, so a new project leaves it alone.
    dropdown.setAttribute('ignore-reset', '');
    const disabledAttr = this.getAttribute('disabled');
    if (disabledAttr !== null) dropdown.setAttribute('disabled', disabledAttr);

    for (const [code, abbrev] of Object.entries(SUPPORTED_LANGS)) {
      const option = document.createElement('as-option');
      option.setAttribute('value', code);
      option.textContent = abbrev;
      if (code === this._currentLanguage) option.setAttribute('selected', '');
      dropdown.appendChild(option);
    }

    const tooltip = document.createElement('as-tooltip-wrapper');
    tooltip.setAttribute('show-on-hover', '');
    tooltip.setAttribute('close-on-click', '');
    tooltip.setAttribute('block', '');
    tooltip.setAttribute('placement', 'bottom');
    const defaultText = this.getAttribute('label-default-text');
    if (defaultText !== null) tooltip.setAttribute('default-text', defaultText);
    const dictKey = this.getAttribute('label-dict-key');
    if (dictKey !== null) tooltip.setAttribute('dict-key', dictKey);
    tooltip.appendChild(dropdown);

    this.appendChild(tooltip);
    this._dropdown = dropdown;
    this._tooltip = tooltip;
  }

  /**
   * Set language programmatically without dispatching as-change-language —
   * this is for syncing with as-app, not user interaction. Silently no-ops
   * for languages outside SUPPORTED_LANGS.
   */
  public setLanguage(lang: string): void {
    if (!SUPPORTED_LANGS[lang] || this._currentLanguage === lang || !this._dropdown) return;
    this._currentLanguage = lang;
    this._dropdown.setValue(lang, { silenceEvents: true });
  }
}

if (!customElements.get(AsLangSelector.tag)) {
  customElements.define(AsLangSelector.tag, AsLangSelector);
} else {
  console.warn("as-lang-selector already defined");
}
